import { Signin } from "../pages/auth/Signin";
import { Loading } from "../components/Loading";
import TokenExpiredScreen from "../components/TokenExpiredScreen";
import { createContext, useLayoutEffect, useState } from "react";
import PropTypes from "prop-types";
import axiosInstance from "../configs/axios.config";
import {
  getSubHeadingCandidates,
  normalizeRoleAccessSections,
} from "../utils/permissionAliases";
const SERVER = import.meta.env.VITE_API_URL;
export const AuthContext = createContext();

const AuthValidationLayer = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [tokenExpired, setTokenExpired] = useState(false);

  useLayoutEffect(() => {
    const validateAuthSession = async () => {
      try {
        let token = localStorage.getItem("prima_session");
        // console.log("[AuthValidation] Raw token from localStorage:", token);

        if (!token || token === "undefined" || token === "null") {
          console.log(
            "[AuthValidation] No valid token found, clearing localStorage",
          );
          localStorage.removeItem("prima_session");
          localStorage.removeItem("prima_user"); // Also clear cached user data
          setLoading(false);
          return;
        }

        // Sanitize token: remove quotes, whitespace, and accidental Bearer prefix
        token = token.trim();
        if (token.startsWith('"') && token.endsWith('"')) {
          token = token.slice(1, -1);
        }
        if (token.startsWith("'") && token.endsWith("'")) {
          token = token.slice(1, -1);
        }
        if (token.startsWith("Bearer ")) {
          token = token.substring(7);
        }
        token = token.trim();

        console.log("[AuthValidation] Sanitized token length:", token.length);

        setToken(token);

        // Try to restore user from cache first
        const cachedUser = localStorage.getItem("prima_user");
        if (cachedUser) {
          try {
            const userData = JSON.parse(cachedUser);
            setUser({
              ...userData,
              roleId: userData?.roleId
                ? {
                  ...userData.roleId,
                  roleAccess: normalizeRoleAccessSections(
                    userData.roleId.roleAccess,
                  ),
                }
                : userData?.roleId,
            });
            console.log("[AuthValidation] Restored user from cache");
          } catch (e) {
            console.error("[AuthValidation] Failed to parse cached user");
          }
        }

        // Try to validate token with retry logic in background
        let isValidationSuccess = false;
        let retries = 3;

        while (retries > 0 && !isValidationSuccess) {
          try {
            const response = await axiosInstance.get(`${SERVER}/validate/token`);

            const data = response.data;
            console.log("[AuthValidation] Response:", data);

            if (data.data) {
              setUser({
                ...data.data,
                roleId: data.data?.roleId
                  ? {
                    ...data.data.roleId,
                    roleAccess: normalizeRoleAccessSections(
                      data.data.roleId.roleAccess,
                    ),
                  }
                  : data.data?.roleId,
              });
              // Cache user data in localStorage
              localStorage.setItem(
                "prima_user",
                JSON.stringify({
                  ...data.data,
                  roleId: data.data?.roleId
                    ? {
                      ...data.data.roleId,
                      roleAccess: normalizeRoleAccessSections(
                        data.data.roleId.roleAccess,
                      ),
                    }
                    : data.data?.roleId,
                }),
              );
              isValidationSuccess = true;
            }
          } catch (error) {
            console.error("[AuthValidation] Error:", error);
            // If it's a 401 or 403, show token expired screen
            if (error.response?.status === 401 || error.response?.status === 403) {
              console.log("[AuthValidation] Session invalidated - clearing local storage");
              localStorage.removeItem("prima_session");
              localStorage.removeItem("prima_user");
              setToken(null);
              setUser(null);
              setTokenExpired(true);
              isValidationSuccess = true; // Stop retrying on auth failures
            } else {
              // Server error or other issue - retry
              retries--;
              if (retries > 0) {
                console.log(
                  "[AuthValidation] Retrying token validation...",
                  retries,
                );
                await new Promise((resolve) => setTimeout(resolve, 500));
              }
            }
          }
        }

        // If validation never succeeded and we still have a token, keep user logged in
        // (token is still set in state, will be used for API calls)
      } catch (error) {
        console.error(
          "[AuthValidation] Outer error during token validation:",
          error,
        );
        // Don't clear token on generic errors - keep session alive
      }

      setLoading(false);
    };

    // Validate authentication session on load
    validateAuthSession();
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  if (tokenExpired) {
    return <TokenExpiredScreen onSignin={() => setTokenExpired(false)} />;
  }

  // Show login page only if there's no token
  // User data will be fetched on demand if needed
  if (!token) {
    return <Signin />;
  }

  const hasPermission = (heading, subHeading, method) => {
    // Superadmin always has all permissions
    if (user?.roleId?.roleName?.toLowerCase() === "superadmin") {
      return true;
    }

    if (!user?.roleId?.roleAccess?.length) return false;

    const headingLower = heading.toLowerCase();
    const subHeadingCandidates = getSubHeadingCandidates(
      heading,
      subHeading,
    ).map((entry) => entry.toLowerCase());

    return user.roleId.roleAccess.some(
      (section) =>
        section.heading?.toLowerCase() === headingLower &&
        section.types?.some(
          (type) =>
            subHeadingCandidates.includes(type.subHeading?.toLowerCase()) &&
            type.access
              ?.map((a) => a.toUpperCase())
              .includes(method.toUpperCase()),
        ),
    );
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        role: user?.roleId,
        roleAccess: user?.roleId?.roleAccess || [],
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
AuthValidationLayer.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthValidationLayer;
